<template>
  <FileUpload
    ref="uploader"
    :name="`${step.name}[${data.element_details.name}]`"
    :customUpload="true"
    :multiple="data.isMultipleFileUpload"
    :accept="accept"
    :maxFileSize="maxFileSize"
    :showCancelButton="false"
    @select="onFileSelect"
    @remove="onRemove"
  >
    <template #empty>
      <div class="p-fileupload-content--emtpy" v-if="!prefillActive">
        <span>Ziehen Sie Dateien zum Hochladen hierher.</span>
      </div>
    </template>
    <template #content>
      <div class="p-fileupload-content--has-content">
        <div v-for="(file, idx) in files" :key="file.name + idx" style="margin-bottom: 8px;">
          <a v-if="file.objectURL" class="p-fileupload-file" style="text-decoration:none;" :href="file.objectURL" target="_blank">
            <Button label="Datei anzeigen" severity="info" />
          </a>
          <span style="margin-left: 8px;">{{ file.name }}</span>
          <button @click="removeFile(idx)" class="p-button p-component p-button-icon-only p-button-danger p-button-rounded p-button-text p-fileupload-file-remove-button" type="button">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M8.01186 7.00933L12.27 2.75116C12.341 2.68501 12.398 2.60524 12.4375 2.51661C12.4769 2.42798 12.4982 2.3323 12.4999 2.23529C12.5016 2.13827 12.4838 2.0419 12.4474 1.95194C12.4111 1.86197 12.357 1.78024 12.2884 1.71163C12.2198 1.64302 12.138 1.58893 12.0481 1.55259C11.9581 1.51625 11.8617 1.4984 11.7647 1.50011C11.6677 1.50182 11.572 1.52306 11.4834 1.56255C11.3948 1.60204 11.315 1.65898 11.2488 1.72997L6.99067 5.98814L2.7325 1.72997C2.59553 1.60234 2.41437 1.53286 2.22718 1.53616C2.03999 1.53946 1.8614 1.61529 1.72901 1.74767C1.59663 1.88006 1.5208 2.05865 1.5175 2.24584C1.5142 2.43303 1.58368 2.61419 1.71131 2.75116L5.96948 7.00933L1.71131 11.2675C1.576 11.403 1.5 11.5866 1.5 11.7781C1.5 11.9696 1.576 12.1532 1.71131 12.2887C1.84679 12.424 2.03043 12.5 2.2219 12.5C2.41338 12.5 2.59702 12.424 2.7325 12.2887L6.99067 8.03052L11.2488 12.2887C11.3843 12.424 11.568 12.5 11.7594 12.5C11.9509 12.5 12.1346 12.424 12.27 12.2887C12.4053 12.1532 12.4813 11.9696 12.4813 11.7781C12.4813 11.5866 12.4053 11.403 12.27 11.2675L8.01186 7.00933Z" fill="currentColor"></path></svg>
          </button>
        </div>
        <div v-if="prefillActive && prefillError != ''" class="p-error" style="margin-top:8px">{{ prefillError }}</div>
      </div>
    </template>
  </FileUpload>
</template>

<script setup>
import { ref, onMounted, watch, onBeforeUnmount } from 'vue'
import axios from 'axios'

const props = defineProps({
  data: Object,
  field: Object,
  step: Object,
  modelValue: { type: [File, String, Object, null], default: null },
  prefillUrl: { type: String, default: '' },
  accept: { type: String, default: 'image/*' },
  maxFileSize: { type: Number, default: 1000000 }
})

const emit = defineEmits(['update:modelValue', 'update:formFiles', 'update:autoheight'])

const uploader = ref(null)
const files = ref([])
const objectUrls = ref([])
const prefillError = ref('')
const prefillActive = ref(false)

function attachObjectURL(file) {
  if (!file.objectURL) {
    const url = URL.createObjectURL(file)
    file.objectURL = url
    objectUrls.value.push(url)
  }
}
function revokeObjectURLs() {
  objectUrls.value.forEach(url => URL.revokeObjectURL(url))
  objectUrls.value = []
}

function deriveFilename(url, contentDisposition) {
  if (contentDisposition) {
    const m = /filename\*?=(?:UTF-8'')?("?)([^";]+)\1/i.exec(contentDisposition);
    if (m?.[2]) return decodeURIComponent(m[2]);
  }
  const last = url.split('?')[0].split('/').pop();
  return decodeURIComponent(last || 'prefilled');
}

async function prefillFromUrl(url) {
  prefillError.value = '';
  prefillActive.value = true;
  revokeObjectURLs();

  try {
    const res = await axios.get(url, { responseType: 'blob' });

    var filename = deriveFilename(url, res.headers?.['content-disposition']).split('/');
    filename = filename[filename.length -1];
    const mime = (res.data && res.data.type) || res.headers?.['content-type'] || undefined;
    const file = new File([res.data], filename, { type: mime });
    attachObjectURL(file);
    files.value = [file];
  } catch (e) {
    prefillError.value = 'Vordefinierte Datei konnte nicht geladen werden.';
    files.value = [];
  }
}

function onFileSelect(event) {
  revokeObjectURLs();
  prefillActive.value = false;
  const selectedFiles = event.files || [];
  selectedFiles.forEach(attachObjectURL);

  files.value = selectedFiles;

  if (!props.data.isMultipleFileUpload) {
      files.value = selectedFiles.slice(-1);
  }
}

function onRemove() {
  clearFiles()
}
function clearFiles() {
  files.value = []
  revokeObjectURLs()
}

function removeFile(idx) {
  // console.log('removeFile', idx);
  const file = files.value[idx];
  if (file?.objectURL) {
    URL.revokeObjectURL(file.objectURL);
    objectUrls.value = objectUrls.value.filter(url => url !== file.objectURL);
  }
  files.value.splice(idx, 1);
  // console.log(files);
}

onMounted(() => {
  if (props.field.value) {
  if (props.field.value instanceof File) files.value = [props.field.value];
  else if (Array.isArray(props.field.value)) files.value = [...props.field.value];
  else if (typeof props.field.value === 'string' && /^https?:\/\//.test(props.field.value)) prefillFromUrl(props.field.value);
  } else if (props.prefillUrl) {
    prefillFromUrl(props.prefillUrl);
  }
})

watch(() => props.prefillUrl, (next, prev) => {
  if (next && next !== prev) prefillFromUrl(next)
})

watch(files, (newFiles) => {
  // console.log('files changes', newFiles);
  emit('update:modelValue', newFiles);
  emit('update:formFiles', props.step, props.data, newFiles);
  emit('update:autoheight');
});

onBeforeUnmount(() => {
  revokeObjectURLs()
})
</script>
